// Live Lightspeed status colors, keyed by lowercased status name. Populated once
// after login from /api/tickets/status-meta (see App.js). Module-level so
// statusPill can stay a pure function of the status name and all nine of its
// call sites are unchanged.
let _statusColors = {};
export function setStatusColors(map) { _statusColors = map || {}; }

// ── What colour a status pill is ─────────────────────────────────────────────
//
// Four steps, in order of how much each actually knows:
//
//   1. The shop's REAL Lightspeed colour, so a status looks the same here as in
//      the POS. Always right when present.
//   2. A literal map of Loose Wheel's status names — covers the moment before
//      status-meta loads, and knows only this shop's names.
//   3. Meaning inferred from the words in the name.
//   4. A stable colour derived from the name.
//
// Steps 3 and 4 exist for shop #2. Before them the last resort was a flat
// '#888', so a shop with its own status names and no colours set in Lightspeed
// opened the service queue with every state identical grey — the first screen
// their staff would ever see, carrying no information at all.
//
// KEYWORDS COME BEFORE THE HASH DELIBERATELY. A hash is distinguishable, not
// meaningful, and would cheerfully paint "Canceled" green. Where the name says
// what it means, use that; where it does not, be distinct and consistent rather
// than pretend to know.
const STATUS_KEYWORDS = [
  [/(cancel|void|abandon)/i,                       '#555555'],
  [/(paid|complete|finish|collect|closed|done)/i,  '#2ecc71'],
  [/(ready|pickup|pick up)/i,                      '#27ae60'],
  [/(priority|urgent|rush|overdue|late)/i,         '#e74c3c'],
  [/(wait|hold|order|parts|approval|confirm|estimate|quote)/i, '#e67e22'],
  [/(review|manager|inspect)/i,                    '#9b59b6'],
  [/(storage|layaway|demo|rental|credit)/i,        '#1abc9c'],
  [/(open|new|intake|received|progress|bench|service|repair)/i, '#3498db'],
];

// Muted and deliberately non-semantic: nothing here reads as "good" or "bad",
// because at this point we genuinely do not know which it is.
const STATUS_PALETTE = [
  '#5b7c99', '#7a6f9b', '#8a7f6d', '#4f8a8b', '#96707f',
  '#6b8e6b', '#8b7355', '#5f7a8a', '#7f7f9b', '#87796b',
];

// Same name, same colour, every session and every screen. A colour that moved
// between renders would be worse than grey.
function hashPick(name, palette) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

export function statusColor(s) {
  const name = String(s || '').trim();
  if (!name) return '#888888';

  const live = _statusColors[name.toLowerCase()];
  if (live) return live;

  const known = { 'Open':'#3498db','Priority!':'#e74c3c','Estimate':'#9b59b6','Waiting':'#e67e22','Parts on Order':'#e67e22','Warranty Submission':'#e67e22','Special Order':'#e67e22','Winter Storage':'#3498db','Customer Confirm':'#e67e22','Manager Review':'#9b59b6','Credit Account':'#95a5a6','Demo/Rental':'#1abc9c','Layaway':'#1abc9c','Finished':'#2ecc71','Finished/Paid':'#2ecc71','Done & Paid':'#2ecc71','Canceled':'#555' };
  if (known[name]) return known[name];

  for (const [pattern, colour] of STATUS_KEYWORDS) {
    if (pattern.test(name)) return colour;
  }
  return hashPick(name.toLowerCase(), STATUS_PALETTE);
}

// Lightspeed's palette runs from near-black to pale tan, so a fixed text color
// is unreadable on one end or the other. Pick by perceived luminance (ITU-R
// BT.601), the same rule Lightspeed's own status list appears to follow.
export function readableOn(hex) {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  if (full.length !== 6) return '#ffffff';
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#111111' : '#ffffff';
}

export const S = {
  app: { minHeight:'100vh', background:'#111827', color:'#e8e4dc', fontFamily:"Arial, sans-serif", fontSize:14 },
  sidebar: { width:220, background:'#1a2336', borderRight:'1px solid #243050', display:'flex', flexDirection:'column', position:'fixed', top:0, left:0, height:'100vh', zIndex:100, overflow:'hidden' },
  sideLogoWrap: { padding:'0 20px 28px', borderBottom:'1px solid #243050', marginBottom:16 },
  sideLogo: { display:'flex', alignItems:'center', gap:10 },
  sideLogoIcon: { width:34, height:34, background:'#e8e4dc', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' },
  sideLogoText: { fontFamily:'Arial, sans-serif', fontSize:13, fontWeight:700, color:'#e8e4dc', lineHeight:1.3 },
  sideLogoSub: { fontFamily:'Arial, sans-serif', fontSize:10, color:'#5a6a8a', marginTop:2 },
  navItem: (active) => ({ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', cursor:'pointer', color: active ? '#e8e4dc' : '#5a6a8a', background: active ? 'rgba(230,126,34,0.12)' : 'transparent', borderRadius:7, fontSize:13, transition:'all 0.15s', userSelect:'none' }),
  // marginLeft matches the sidebar exactly (220) and horizontal padding matches
  // the tables' cell padding (14). Together that puts the page title, the KPI
  // cards, the toolbar and the first table column all on the same left edge at
  // 234px — previously the cards sat at 258 and the toolbar at 234, close enough
  // to look like a misalignment rather than a choice.
  //
  // It also makes every bleed on the queue screens exactly PAGE_PAD, so the
  // tables can run to the window edges by cancelling one number rather than
  // three. Vertical padding is unchanged — the top gap is doing real work
  // separating the page title from the browser chrome.
  main: { marginLeft:220, padding:'32px 14px', minHeight:'100vh' },
  topbar: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:28 },
  pageTitle: { fontFamily:'Arial, sans-serif', fontSize:22, fontWeight:700, color:'#e8e4dc' },
  syncInfo: { display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#5a6a8a' },
  syncDot: (live) => ({ width:7, height:7, borderRadius:'50%', background: live ? '#2ecc71' : '#e67e22' }),
  kpiGrid: { display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:12, marginBottom:28 },
  kpi: (accent) => ({ background:'#1a2336', border:'1px solid #243050', borderRadius:10, padding:'18px 20px', borderTop: accent ? `3px solid ${accent}` : '1px solid #243050' }),
  kpiLabel: { fontSize:11, color:'#5a6a8a', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 },
  kpiValue: (color) => ({ fontFamily:'Arial, sans-serif', fontSize:32, fontWeight:700, color: color || '#e8e4dc', lineHeight:1 }),
  kpiSub: { fontSize:11, color:'#3a4a6a', marginTop:6 },
  section: { marginBottom:28 },
  sectionHead: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 },
  sectionTitle: { fontFamily:'Arial, sans-serif', fontSize:12, fontWeight:700, color:'#5a6a8a', textTransform:'uppercase', letterSpacing:'0.08em' },
  filterRow: { display:'flex', gap:6 },
  filterBtn: (active) => ({ fontSize:12, padding:'5px 14px', borderRadius:20, border: active ? 'none' : '1px solid #2e3d58', background: active ? '#e67e22' : 'transparent', color: active ? '#ffffff' : '#5a6a8a', cursor:'pointer', fontFamily:'Arial, sans-serif', transition:'all 0.15s' }),
  table: { width:'100%', borderCollapse:'collapse' },
  th: { fontSize:11, textTransform:'uppercase', letterSpacing:'0.06em', color:'#4a5a7a', fontWeight:600, padding:'8px 14px', textAlign:'left', borderBottom:'1px solid #243050', whiteSpace:'nowrap' },
  td: { fontSize:13, padding:'11px 14px', borderBottom:'1px solid #1e2a40', color:'#ccd0dc', verticalAlign:'middle' },
  tdClickable: { fontSize:13, padding:'11px 14px', borderBottom:'1px solid #1e2a40', color:'#ccd0dc', verticalAlign:'middle', cursor:'pointer' },
  mono: { fontFamily:"'IBM Plex Mono', monospace", fontSize:12, color:'#5a6a8a' },
  badge: (level) => {
    const styles = {
      danger:  { background:'#2a0f0f', color:'#e74c3c' },
      warning: { background:'#2a1f0a', color:'#e67e22' },
      revenue: { background:'#1a2a0a', color:'#f1c40f' },
    };
    const s = styles[level] || styles.danger;
    return { fontSize:11, padding:'3px 9px', borderRadius:4, fontWeight:500, background:s.background, color:s.color, whiteSpace:'nowrap', display:'inline-block', marginRight:4, marginBottom:2 };
  },
  badgeClean: { fontSize:11, padding:'3px 9px', borderRadius:4, fontWeight:500, background:'#0f2a15', color:'#2ecc71', display:'inline-block' },
  statusPill: (s) => {
    const color = statusColor(s);
    return { fontSize:11, padding:'3px 10px', borderRadius:20, fontWeight:600,
      background: color, color: readableOn(color), whiteSpace:'nowrap' };
  },
  riskBar: { width:80, height:5, background:'#243050', borderRadius:3, overflow:'hidden' },
  riskFill: (score) => ({ height:'100%', borderRadius:3, width:`${score}%`, background: score >= 40 ? '#e74c3c' : score >= 20 ? '#e67e22' : '#2ecc71' }),
  days: (n) => ({ fontFamily:"'IBM Plex Mono', monospace", fontSize:12, fontWeight:500, color: n > 4 ? '#e74c3c' : n >= 3 ? '#e67e22' : '#5a6a8a' }),
  chartGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:28 },
  chartCard: { background:'#1a2336', border:'1px solid #243050', borderRadius:10, padding:'18px 20px' },
  chartTitle: { fontFamily:'Arial, sans-serif', fontSize:12, fontWeight:700, color:'#5a6a8a', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:16 },
  barRow: { display:'flex', alignItems:'center', gap:10, marginBottom:10 },
  barLabel: { fontSize:12, color:'#8a9ab8', minWidth:120, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' },
  barTrack: { flex:1, height:6, background:'#243050', borderRadius:3, overflow:'hidden' },
  barFill: (color, pct) => ({ height:'100%', width:`${pct}%`, background:color, borderRadius:3 }),
  barCount: { fontSize:12, color:'#5a6a8a', minWidth:20, textAlign:'right' },
  btn: (primary) => ({ padding: primary ? '10px 20px' : '8px 16px', borderRadius:8, border: primary ? 'none' : '1px solid #2e3d58', background: primary ? '#e67e22' : 'transparent', color: primary ? '#ffffff' : '#8a9ab8', fontFamily:'Arial, sans-serif', fontSize:13, fontWeight:700, cursor:'pointer', transition:'all 0.15s' }),
  emptyRow: { textAlign:'center', padding:'32px', color:'#4a5a7a', fontSize:13 },
  overlay: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.7)', zIndex:200, display:'flex', justifyContent:'flex-end' },
  panel: { width:520, background:'#1a2336', height:'100vh', overflowY:'auto', borderLeft:'1px solid #243050', display:'flex', flexDirection:'column' },
  panelHeader: { padding:'24px 24px 20px', borderBottom:'1px solid #243050', display:'flex', alignItems:'flex-start', justifyContent:'space-between', position:'sticky', top:0, background:'#1a2336', zIndex:10 },
  panelTitle: { fontFamily:'Arial, sans-serif', fontSize:18, fontWeight:700, color:'#e8e4dc' },
  panelSub: { fontSize:12, color:'#5a6a8a', marginTop:4 },
  panelClose: { background:'transparent', border:'none', color:'#5a6a8a', fontSize:22, cursor:'pointer', lineHeight:1, padding:4 },
  panelBody: { padding:'24px', flex:1 },
  panelSection: { marginBottom:24 },
  panelSectionTitle: { fontFamily:'Arial, sans-serif', fontSize:11, fontWeight:700, color:'#4a5a7a', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 },
  infoGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 },
  infoItem: { background:'#1e2a40', borderRadius:8, padding:'12px 14px' },
  infoLabel: { fontSize:11, color:'#5a6a8a', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.05em' },
  infoValue: { fontSize:13, color:'#e8e4dc', fontWeight:500 },
  lineItem: { background:'#1e2a40', borderRadius:8, padding:'12px 14px', marginBottom:8 },
  lineItemHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 },
  lineItemTech: { fontSize:11, color:'#5a6a8a' },
  lineItemPrice: { fontFamily:"'IBM Plex Mono', monospace", fontSize:13, color:'#2ecc71', fontWeight:500 },
  lineItemNote: { fontSize:12, color:'#8a9ab8', lineHeight:1.5 },
  lineItemDone: { fontSize:11, padding:'2px 8px', borderRadius:4, background:'#0f2a15', color:'#2ecc71', display:'inline-block', marginTop:6 },
  noteBox: { background:'#1e2a40', borderRadius:8, padding:'14px', fontSize:13, color:'#9aa8c0', lineHeight:1.6, whiteSpace:'pre-wrap', wordBreak:'break-word' },
  totalRow: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px', background:'#243050', borderRadius:8, marginTop:8 },
  totalLabel: { fontSize:13, color:'#8a9ab8' },
  totalValue: { fontFamily:'Arial, sans-serif', fontSize:20, fontWeight:700, color:'#2ecc71' },
  loadingPanel: { display:'flex', alignItems:'center', justifyContent:'center', flex:1, color:'#5a6a8a', fontSize:13 },
  settingsCard: { background:'#1a2336', border:'1px solid #243050', borderRadius:10, padding:'24px', marginBottom:20 },
  settingsCardTitle: { fontFamily:'Arial, sans-serif', fontSize:14, fontWeight:700, color:'#e8e4dc', marginBottom:6 },
  settingsCardDesc: { fontSize:12, color:'#5a6a8a', marginBottom:20, lineHeight:1.6 },
  statusGrid: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10 },
  statusToggle: (excluded) => ({ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderRadius:8, border: excluded ? '1px solid #e74c3c33' : '1px solid #2ecc7133', background: excluded ? '#2a0f0f' : '#0f2a15', cursor:'pointer', transition:'all 0.15s' }),
  statusToggleName: (excluded) => ({ fontSize:13, color: excluded ? '#e74c3c' : '#2ecc71', fontWeight:500 }),
  statusToggleState: (excluded) => ({ fontSize:11, color: excluded ? '#e74c3c88' : '#2ecc7188' }),
  thresholdRow: { display:'flex', alignItems:'center', gap:16, marginBottom:16 },
  thresholdLabel: { fontSize:13, color:'#ccd0dc', minWidth:200 },
  thresholdInput: { width:60, padding:'6px 10px', background:'#243050', border:'1px solid #2e3d58', borderRadius:6, color:'#e8e4dc', fontSize:14, fontFamily:"'IBM Plex Mono', monospace", textAlign:'center' },
  thresholdUnit: { fontSize:12, color:'#5a6a8a' },
  saveBar: { display:'flex', alignItems:'center', gap:12, marginTop:24 },
  savedMsg: { fontSize:12, color:'#2ecc71' },
};
